import React, { useEffect, useRef } from 'react';
import { Gradient } from 'stripe-gradient';
import NumberFlow from '@number-flow/react';
import { Block, Inline } from '@contentful/rich-text-types';
import SxProps from 'threads5/themes/sx-props';
import type { ITheme } from 'threads5/themes/theme';
import { animated, useReducedMotion } from '@react-spring/web';
import type { SpringValue } from '@react-spring/web';

import {
  formatNumber,
  makeImageSource,
  makeImageQueryString,
} from 'src/lib/utils';
import { isAutomatedBrowser } from 'src/lib/browser-utils';
import Stack from 'src/components-v2/Layout/Stack/Stack';
import {
  Card,
  Canvas,
  CanvasWrapper,
  CardCtaButton,
  Disclaimer,
} from './MortgageCalculator.styled';
import CmsMediaSwitch from 'src/components-v2/Media/CmsMediaSwitch/CmsMediaSwitch';
import Image from 'src/components-v2/Media/Image/Image';
import RichTextSwitch from 'src/containers/RichTextSwitch/RichTextSwitch';
import { Typography } from 'src/components-v2/DataDisplay/Typography';
import type { ICalculation } from './MortgageCalculator.types';
import type { Sys } from 'src/types/contentful-sdk-compat';
import type { ComponentAsset } from 'src/lib/ts-types';
import Box from 'src/components-v2/Layout/Box/Box';
import { targetMap } from 'src/lib/constants';
import type { Button2Entry } from 'src/types/contentful';

const AnimatedStack = animated(Stack);

const ERROR_MESSAGE =
  "We need a bit more info to make an estimate. Let's take a closer look together.";

const SPOT_IMAGE_URL =
  'https://images.ctfassets.net/ss5kfr270og3/k1m6JmvViVpIdhxE34EPS/777c7daa0c95b8520cddc18e17a8e78b/Spot-Magnify-001.png';

const SPOT_IMAGE_WIDTH = 230;
const SPOT_IMAGE_HEIGHT = 240;
const SPOT_IMAGE_QUALITY = 100;

const spotImageSources = {
  xs: makeImageQueryString({
    src: SPOT_IMAGE_URL,
    width: SPOT_IMAGE_WIDTH,
    quality: SPOT_IMAGE_QUALITY,
  }),
  sm: makeImageQueryString({
    src: SPOT_IMAGE_URL,
    width: SPOT_IMAGE_WIDTH,
    quality: SPOT_IMAGE_QUALITY,
  }),
  md: makeImageQueryString({
    src: SPOT_IMAGE_URL,
    width: SPOT_IMAGE_WIDTH,
    quality: SPOT_IMAGE_QUALITY,
  }),
  lg: makeImageQueryString({
    src: SPOT_IMAGE_URL,
    width: SPOT_IMAGE_WIDTH,
    quality: SPOT_IMAGE_QUALITY,
  }),
  xl: makeImageQueryString({
    src: SPOT_IMAGE_URL,
    width: SPOT_IMAGE_WIDTH,
    quality: SPOT_IMAGE_QUALITY,
  }),
};

interface MortgageCardProps {
  hasError: boolean;
  isPotato: boolean;
  errorSpring?: { opacity: SpringValue<number>; x: SpringValue<number> };
  errorMessageSpring?: {
    opacity: SpringValue<number>;
    x: SpringValue<number>;
  };
  spotImageSpring?: {
    opacity: SpringValue<number>;
    x: SpringValue<number>;
  };
  calcState: ICalculation;
  sys?: Sys;
  inspectorProps?: (args: { fieldId: string }) => Record<string, unknown>;
  fields?: {
    cardBackgroundAsset?: ComponentAsset;
    cardCtaButton: Button2Entry;
    cardDisclaimer?: Block | Inline;
    trackingContext?: Record<string, unknown>;
    styles?: SxProps<ITheme>;
  };
}

export const MortgageCard: React.FC<MortgageCardProps> = ({
  calcState,
  fields,
  hasError,
  inspectorProps,
  isPotato,
  sys,
  errorSpring,
  errorMessageSpring,
  spotImageSpring,
}) => {
  const canvasWrapperRef = useRef<HTMLDivElement>(null);

  const errorStyle =
    errorSpring &&
    ({
      opacity: errorSpring.opacity,
      transform: errorSpring.x.to((x) => {
        return `translateX(${x}px)`;
      }),
    } as unknown as React.CSSProperties);
  const errorMessageStyle =
    errorMessageSpring &&
    ({
      opacity: errorMessageSpring.opacity,
      transform: errorMessageSpring.x.to((x) => {
        return `translateX(${x}px)`;
      }),
    } as unknown as React.CSSProperties);
  const spotImageStyle =
    spotImageSpring &&
    ({
      opacity: spotImageSpring.opacity,
      transform: spotImageSpring.x.to((x) => {
        return `translateX(${x}px)`;
      }),
    } as unknown as React.CSSProperties);
  const { lgImgSrc, mdImgSrc, smImgSrc } = makeImageSource(
    fields?.cardBackgroundAsset?.fields,
  );

  const ctaButton = fields?.cardCtaButton?.fields;
  const ctaHref = ctaButton?.url;
  const ctaText = ctaButton?.text ?? 'Learn more';
  const ctaTargetRaw =
    ctaButton?.target && targetMap[ctaButton.target as keyof typeof targetMap];
  const ctaTarget: '_self' | '_blank' =
    ctaTargetRaw === '_blank' ? '_blank' : '_self';

  // Gradient button animation: use unique id so multiple calculators on the same page don't conflict (ACH also uses a gradient canvas)
  const gradientCanvasId = 'gradient-mortgage-calculator';
  const prefersReducedMotion = useReducedMotion();
  useEffect(() => {
    if (!ctaHref) return;
    const gradient = new Gradient();
    // Freeze to one deterministic frame for reduced-motion users, low-perf
    // devices, or automated capture (see `isAutomatedBrowser`). `isStatic`
    // renders a single seeded frame, then disconnects.
    if (isPotato || prefersReducedMotion || isAutomatedBrowser()) {
      (gradient as { isStatic?: boolean }).isStatic = true;
    }
    gradient.initGradient(`#${gradientCanvasId}`);
    return () => {
      (gradient as { disconnect?: () => void }).disconnect?.();
    };
  }, [ctaHref, isPotato, prefersReducedMotion]);

  return (
    <Card
      className='cms-mortgage-calculator-card'
      sx={{ padding: { xs: '20px', sm: '40px' } }}
    >
      <Stack
        className='cms-mortgage-calculator-card-stack'
        sx={{
          gap: 4.25,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          zIndex: 1,
        }}
      >
        <Box
          className='cms-mortgage-calculator-card-stats'
          sx={{ position: 'relative' }}
        >
          <AnimatedStack
            className='cms-mortgage-calculator-card-stats-inner'
            flexDirection='column'
            style={errorStyle}
            sx={errorStyle ? undefined : { opacity: hasError ? '0' : '1' }}
            aria-hidden={hasError}
          >
            <Typography
              className='cms-mortgage-calculator-card-stats-label'
              variant='h4'
              component='span'
              sx={{ fontSize: { xs: '2.4rem', sm: '2.8rem' } }}
            >
              Potential annual savings
            </Typography>
            <Typography
              className='cms-mortgage-calculator-card-stats-value'
              variant='h2'
              component='span'
              sx={{
                fontSize: { xs: '4.0rem', sm: '6.0rem' },
                mb: { xs: '14px', sm: '20px' },
              }}
            >
              <NumberFlow
                animated={
                  !isPotato && !prefersReducedMotion && !isAutomatedBrowser()
                }
                value={calcState.savings}
                format={{
                  style: 'currency',
                  currency: 'USD',
                  trailingZeroDisplay: 'stripIfInteger',
                }}
              />
            </Typography>
            <Stack
              className='cms-mortgage-calculator-card-loan-volume'
              gap={{ xs: 2, sm: 2.5 }}
            >
              <hr />
              <Typography
                className='cms-mortgage-calculator-card-loan-volume-label'
                variant='h4'
                component='span'
                sx={{
                  fontSize: '2.8rem',
                }}
              >
                Loan volume impact
              </Typography>
              <Stack
                className='cms-mortgage-calculator-card-loan-volume-row'
                direction='row'
                gap={1}
                justifyContent='space-between'
                sx={{
                  'p, span': {
                    fontSize: { xs: '1.4rem', sm: '1.8rem' },
                  },
                }}
              >
                <Typography variant='p5' component='span'>
                  Additional verified applications
                </Typography>
                <Typography variant='p5' component='span'>
                  {formatNumber({
                    value: calcState.additionalVerifiedApplications,
                  })}
                </Typography>
              </Stack>
            </Stack>
          </AnimatedStack>
          {errorMessageStyle && (
            <AnimatedStack
              className='cms-mortgage-calculator-card-error-message'
              component='div'
              style={errorMessageStyle}
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'center',
                pointerEvents: hasError ? 'auto' : 'none',
                zIndex: 1,
              }}
              aria-hidden={!hasError}
              aria-live='polite'
              role='status'
            >
              <Typography
                className='cms-mortgage-calculator-card-error-text'
                variant='h4'
                component='p'
                sx={{ fontSize: { xs: '2.4rem', sm: '2.8rem' } }}
              >
                {ERROR_MESSAGE}
              </Typography>
            </AnimatedStack>
          )}
        </Box>

        {ctaHref && (
          <CanvasWrapper
            ref={canvasWrapperRef}
            className='cms-mortgage-calculator-card-cta'
            sx={{ position: 'relative' }}
            {...(inspectorProps?.({ fieldId: 'cardCtaButton' }) ?? {})}
            onKeyDown={(e) => {
              if (e.key !== ' ') return;
              const wrapper = canvasWrapperRef.current;
              if (!wrapper?.contains(e.target as Node)) return;
              e.preventDefault();
              wrapper.querySelector<HTMLAnchorElement>('a')?.click();
            }}
          >
            <CardCtaButton
              className='cms-mortgage-calculator-card-cta-button'
              href={ctaHref}
              target={ctaTarget}
              // TODO: at some point we should fix this and forward more custom tracking context
              trackingId={ctaButton?.trackingId ?? ''}
              sys={fields?.cardCtaButton?.sys}
            >
              {ctaText}
            </CardCtaButton>
            <Canvas
              id={gradientCanvasId}
              className='cms-mortgage-calculator-card-cta-canvas'
              sx={{ position: 'relative', zIndex: 0 }}
              aria-hidden
            />
          </CanvasWrapper>
        )}
        <AnimatedStack
          className='cms-mortgage-calculator-card-disclaimer-wrap'
          gap={4.25}
          style={errorStyle}
          sx={errorStyle ? undefined : { opacity: hasError ? '0' : '1' }}
          aria-hidden={hasError}
          {...(inspectorProps?.({ fieldId: 'cardDisclaimer' }) ?? {})}
        >
          <hr />
          <Disclaimer className='cms-mortgage-calculator-card-disclaimer'>
            <RichTextSwitch
              sys={sys}
              content={fields?.cardDisclaimer}
              nodes={{
                p: {
                  variant: 'p',
                  component: 'p',
                  fieldId: 'cardDisclaimer',
                },
              }}
            />
          </Disclaimer>
        </AnimatedStack>
      </Stack>
      {spotImageStyle && (
        <AnimatedStack
          className='cms-mortgage-calculator-card-spot-image-wrap'
          component='div'
          style={spotImageStyle}
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            pointerEvents: 'none',
          }}
          aria-hidden
          role='presentation'
        >
          <Image
            className='cms-mortgage-calculator-card-spot-image'
            src={`${SPOT_IMAGE_URL}?fm=webp&q=${SPOT_IMAGE_QUALITY}&w=${
              SPOT_IMAGE_WIDTH * 2
            }`}
            srcset={makeImageQueryString({
              src: SPOT_IMAGE_URL,
              width: SPOT_IMAGE_WIDTH,
            })}
            sources={spotImageSources}
            alt=''
            width={SPOT_IMAGE_WIDTH}
            height={SPOT_IMAGE_HEIGHT}
            isLazy={false}
            sx={{ display: 'block' }}
          />
        </AnimatedStack>
      )}
      <CmsMediaSwitch
        className='cms-mortgage-calculator-card-background'
        role='presentation'
        asset={fields?.cardBackgroundAsset?.fields}
        sys={fields?.cardBackgroundAsset?.sys}
        queryString={{
          xl: `?fm=webp&w=1122&q=60`,
        }}
        sources={{
          xs: makeImageQueryString({ src: smImgSrc, width: 561 }),
          sm: makeImageQueryString({ src: smImgSrc, width: 527 }),
          md: makeImageQueryString({ src: mdImgSrc, width: 1304 }),
          lg: makeImageQueryString({ src: lgImgSrc, width: 816 }),
          xl: makeImageQueryString({ src: lgImgSrc, width: 1122 }),
        }}
        sx={{
          position: 'absolute',
          bottom: 0,
          width: '100%',
          left: 0,
        }}
      />
    </Card>
  );
};
